import classNames from 'classnames';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import ControlBarActivity, {
  CONTROL_BAR_ACTIVITY_MIN_HEIGHT,
} from './ControlBarActivity';
import * as styles from './ControlBarActivityButton.module.scss';
import ControlBarButton from './ControlBarButton';
import ControlBarPopOver from './ControlBarPopOver';
import useAppSelector from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated/gql';
import { AlphaPostActivityType } from '../../graphql/generated/graphql';
import graphStatsIcon from '../../icons/non-prefilled/16x16/graphStats.svg';
import { isFeatureEnabled } from '../../utils/features';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';

const POST_FRAGMENT = gql(`
  fragment ControlBarActivityButton_post on Post {
    id
    insertedAt
    publishedAt
    userViewedAt
  }
`);

const POST_ACTIVITIES_FRAGMENT = gql(`
  fragment ControlBarActivityButton_activity on AlphaPostActivity {
    actedAt
    action
    actors {
      id
    }
  }
`);

interface ControlBarActivityButtonProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  post: DocumentType<typeof POST_FRAGMENT>;
  activities: DocumentType<typeof POST_ACTIVITIES_FRAGMENT>[];
}

const ControlBarActivityButton = ({
  open,
  setOpen,
  post,
  activities,
}: ControlBarActivityButtonProps) => {
  const postRenderTime = useMemo(
    () => ({ postId: post.id, time: moment() }), // Reset when active post changes
    [post.id],
  ).time;

  const currentUser = useAppSelector((state) => state.session.user);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const [lastUserViewBeforeOpen, setLastUserViewBeforeOpen] = useState<
    number | string
  >(post.userViewedAt ?? 0);

  if (
    lastUserViewBeforeOpen !== post.userViewedAt &&
    postRenderTime.isAfter(post.userViewedAt)
  ) {
    setLastUserViewBeforeOpen(post.userViewedAt);
  }

  const lastUserActivityBeforeOpen =
    activities.find(
      (activity) =>
        activity.actors.some((actor) => actor.id === currentUser.id) &&
        postRenderTime.isAfter(activity.actedAt),
    )?.actedAt ?? 0;

  const lastViewedAt = Math.max(
    new Date(lastUserViewBeforeOpen).getTime(),
    new Date(lastUserActivityBeforeOpen).getTime(),
  );

  const hasNewActivities =
    lastViewedAt !== 0 &&
    activities.some((activity) => {
      const isEmptyEdit =
        activity.actors.length === 0 &&
        activity.action === AlphaPostActivityType.Edit;

      return (
        moment(activity.actedAt).isAfter(lastViewedAt) &&
        !isEmptyEdit &&
        !activity.actors.some((actor) => actor.id === currentUser.id)
      );
    });

  const isNewDraft =
    !post.publishedAt &&
    !activities.some((item) => item.action === AlphaPostActivityType.Publish) &&
    moment(post.insertedAt).add(1, 'hour').isAfter(postRenderTime);

  return (
    isFeatureEnabled('post_activity') &&
    !isNewDraft &&
    !isMobile && (
      <ControlBarPopOver
        content={
          <ControlBarActivity
            postId={post.id}
            closePopover={() => setOpen(false)}
            lastViewedAt={lastViewedAt}
          />
        }
        open={open}
        onOpenChange={setOpen}
        placement="bottom-start"
        preventScreenOverflow={{
          minHeight: CONTROL_BAR_ACTIVITY_MIN_HEIGHT,
        }}
      >
        <ControlBarButton
          active={open}
          icon={
            <SVGIcon
              className={classNames({
                [styles.hasNewActivities]: hasNewActivities,
              })}
            >
              {graphStatsIcon}
            </SVGIcon>
          }
        />
      </ControlBarPopOver>
    )
  );
};

export default ControlBarActivityButton;
